import { FunctionComponent, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import { Check, ArrowUpRight, MessageCircle, ChevronDown, ChevronUp } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import axios from '../../utils/axios'
import colors from '../../colors'

/**
 * Onboarding checklist. The dashboard's primary action for a new brewery -
 * it is what leads to payments being taken - so it stays compact and legible
 * rather than sprawling.
 *
 * Two columns, numbered steps, and only the active one expanded: future steps
 * need a title and nothing else, and numbers communicate sequence in a way
 * six different icons did not.
 *
 * Plain elements rather than MUI ones: @material-ui/core v5-beta (emotion)
 * silently overrides @material-ui/styles v4 (JSS) classes, so sizes and
 * colours set here would not apply to a Typography or Button.
 */

interface SetupStatus {
  stripeConnected: boolean
  hasLiveMembership: boolean
  appleWalletEnabled: boolean
  walletConfigured: boolean
  firstMembershipUuid: string | null
  hasLogo: boolean
  /** SMS is a Pro feature, so the step is omitted for Core rather than
   *  shown as an upsell inside a list of things the org can actually do. */
  smsEligible: boolean
  smsSetUp: boolean
  hasMembers: boolean
  hasSentNotification: boolean
  vipLink: string | null
}

interface Step {
  id: string
  title: string
  /** Shown only while this is the active step. */
  description: string
  done: boolean
  /** Internal route. Omitted while a step isn't actionable yet. */
  to?: string
  /** External destination, used for the brewery's own public signup page. */
  href?: string
}

const SUPPORT_EMAIL = 'help@stitch.vip'

const SetupRecommendationsCard: FunctionComponent = () => {
  const styles = useStyles()
  const navigate = useNavigate()
  const [status, setStatus] = useState<SetupStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/organization/setup_status')
        setStatus(res.data)
      } catch {
        // best-effort; the card simply won't render on failure
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  if (loading || !status) return null

  const steps: Step[] = [
    {
      id: 'stripe',
      title: 'Connect Stripe',
      description: 'Accept payments',
      done: status.stripeConnected,
      to: '/settings',
    },
    {
      // Before the wallet step: Apple requires an icon on every pass, so
      // wallet cards cannot be switched on until this is done.
      id: 'logo',
      title: 'Add your logo',
      description: 'Goes on wallet cards and emails',
      done: status.hasLogo,
      to: '/settings/branding',
    },
    {
      id: 'membership',
      title: 'Launch a membership',
      description: 'Set your price and publish',
      done: status.hasLiveMembership,
      to: '/memberships',
    },
    {
      id: 'wallet',
      title: 'Set up Apple Wallet',
      description: "Design your members' card",
      done: status.walletConfigured,
      to: status.firstMembershipUuid ? `/memberships/${status.firstMembershipUuid}/wallet` : '/memberships',
    },
    {
      // Between publishing and notifying: a brewery with a live membership and
      // nobody in it needs to share the page, not write a message.
      id: 'signup',
      title: 'Get your first signup',
      // Shows the actual address rather than "share your signup page" - the
      // brewery's job here is to hand this link out, so it needs to see what
      // the link is. Protocol stripped; it adds nothing and costs width.
      description: status.vipLink
        ? `Have customers sign up at ${status.vipLink.replace(/^https?:\/\//, '')}`
        : 'Have customers sign up at your Stitch page',
      done: status.hasMembers,
      ...(status.vipLink ? { href: status.vipLink } : {}),
    },
    {
      id: 'notify',
      title: 'Send a notification',
      description: status.hasMembers
        ? 'Message members on their phones'
        : 'Available once you have members',
      done: status.hasSentNotification,
      // No route until there is somebody to send to: Notify would only show
      // them an empty recipient list.
      ...(status.hasMembers ? { to: '/notify' } : {}),
    },
  ]

  // Last deliberately: registration takes days of carrier vetting, so it
  // should not be the first thing a new brewery is sent to.
  if (status.smsEligible) {
    steps.push({
      id: 'sms',
      title: 'Set up SMS marketing',
      description: 'Text your members',
      done: status.smsSetUp,
      to: '/settings/sms',
    })
  }

  const completedCount = steps.filter((s) => s.done).length
  const nextStep = steps.find((s) => !s.done)
  const allDone = !nextStep
  const percent = Math.round((completedCount / steps.length) * 100)

  const go = (step: Step) => {
    if (step.href) window.open(step.href, '_blank', 'noreferrer')
    else if (step.to) navigate(step.to)
  }

  // Once everything is done the checklist collapses to a line, openable for
  // anyone who wants to look back over it.
  if (allDone) {
    return (
      <div className={styles.card}>
        <button type="button" className={styles.doneBar} onClick={() => setExpanded(!expanded)}>
          <span className={styles.doneMark}><Check size={14} /></span>
          <span className={styles.doneText}>Setup complete</span>
          <span className={styles.doneToggle}>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </button>

        {expanded && (
          <div className={styles.grid}>
            {steps.map((step) => (
              <div key={step.id} className={styles.tileDone}>
                <span className={styles.markDone}><Check size={13} /></span>
                <span className={styles.titleDone}>{step.title}</span>
              </div>
            ))}
          </div>
        )}

        <Footer styles={styles} />
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Get your brewery up and running</h2>
          <div className={styles.subtitle}>
            Complete these steps to launch your membership program.
          </div>
        </div>
        <div className={styles.count}>{completedCount} of {steps.length}</div>
      </div>

      <div className={styles.track}>
        <div className={styles.fill} style={{ width: `${percent}%` }} />
      </div>

      <div className={styles.grid}>
        {steps.map((step, index) => {
          const isNext = step.id === nextStep?.id
          const actionable = isNext && Boolean(step.to || step.href)

          return (
            <div
              key={step.id}
              role={actionable ? 'button' : undefined}
              tabIndex={actionable ? 0 : undefined}
              onClick={actionable ? () => go(step) : undefined}
              onKeyDown={actionable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(step) }
              } : undefined}
              className={
                step.done ? styles.tileDone
                  : isNext ? (actionable ? styles.tileActive : styles.tileActiveFlat)
                    : styles.tile
              }
            >
              {step.done
                ? <span className={styles.markDone}><Check size={13} /></span>
                : <span className={isNext ? styles.markActive : styles.mark}>{index + 1}</span>}

              <span className={styles.tileBody}>
                <span className={step.done ? styles.titleDone : isNext ? styles.titleActive : styles.titleLater}>
                  {step.title}
                </span>
                {isNext && <span className={styles.tileDesc}>{step.description}</span>}
              </span>

              {actionable && <ArrowUpRight size={16} className={styles.arrow} />}
            </div>
          )
        })}
      </div>

      <Footer styles={styles} />
    </div>
  )
}

// Permanent, so nobody has to go hunting while stuck mid-setup.
const Footer: FunctionComponent<{ styles: any }> = ({ styles }) => (
    <div className={styles.footer}>
      <MessageCircle size={15} className={styles.footerIcon} />
      <span className={styles.footerLead}>Stuck on a step?</span>
      <span className={styles.footerText}>We&apos;re here to help.</span>
      <a className={styles.footerLink} href={`mailto:${SUPPORT_EMAIL}`}>
        Email support <ArrowUpRight size={13} style={{ marginLeft: 2 }} />
      </a>
    </div>
)

const tileBase = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderRadius: 10,
  border: '1px solid transparent',
  minWidth: 0,
}

const markBase = {
  width: 26,
  height: 26,
  flexShrink: 0,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 700,
}

const useStyles = makeStyles({
  card: {
    background: colors.WHITE,
    border: `1px solid ${colors.GREY_5}`,
    borderRadius: 14,
    padding: '20px 22px 14px',
    marginBottom: 24,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  title: {
    fontSize: 19,
    fontWeight: 700,
    color: colors.STITCH_BLACK,
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: colors.GREY_2,
    marginTop: 3,
  },
  count: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.GREY_3,
    whiteSpace: 'nowrap',
    paddingTop: 3,
  },
  track: {
    height: 5,
    borderRadius: 999,
    background: colors.GREY_6,
    overflow: 'hidden',
    margin: '14px 0',
  },
  fill: {
    height: '100%',
    borderRadius: 999,
    background: colors.BRIGHT_BLUE,
    transition: 'width 240ms ease',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    alignItems: 'stretch',
    '@media (max-width: 760px)': {
      gridTemplateColumns: '1fr',
    },
  },
  tile: {
    ...tileBase,
    background: colors.GREY_7,
  },
  tileDone: {
    ...tileBase,
    background: colors.GREY_7,
  },
  tileActive: {
    ...tileBase,
    background: colors.WHITE,
    border: `1px solid ${colors.GREY_4}`,
    boxShadow: '0 1px 3px rgba(15,23,42,0.07)',
    cursor: 'pointer',
    '&:hover': {
      borderColor: colors.BRIGHT_BLUE,
      boxShadow: '0 2px 8px rgba(15,23,42,0.10)',
    },
  },
  // The active step with nowhere to go yet - "Send a notification" before
  // anyone has joined. Looks active, but is not a dead click target.
  tileActiveFlat: {
    ...tileBase,
    background: colors.WHITE,
    border: `1px solid ${colors.GREY_5}`,
  },
  mark: {
    ...markBase,
    background: colors.GREY_6,
    color: colors.GREY_3,
  },
  markActive: {
    ...markBase,
    background: colors.PALE_BLUE,
    color: colors.BRIGHT_BLUE,
  },
  markDone: {
    ...markBase,
    background: colors.PALE_GREEN,
    color: colors.DARK_GREEN,
  },
  tileBody: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  titleActive: {
    fontSize: 15,
    fontWeight: 700,
    color: colors.STITCH_BLACK,
  },
  titleLater: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.GREY_3,
  },
  titleDone: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.GREY_2,
  },
  tileDesc: {
    fontSize: 12,
    color: colors.GREY_3,
    marginTop: 1,
  },
  arrow: {
    flexShrink: 0,
    color: colors.GREY_3,
  },
  doneBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 0 10px',
    fontFamily: 'inherit',
    textAlign: 'left',
  },
  doneMark: {
    ...markBase,
    background: colors.PALE_GREEN,
    color: colors.DARK_GREEN,
  },
  doneText: {
    flex: 1,
    fontSize: 15,
    fontWeight: 700,
    color: colors.STITCH_BLACK,
  },
  doneToggle: {
    color: colors.GREY_3,
    display: 'flex',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    flexWrap: 'wrap',
    borderTop: `1px solid ${colors.GREY_6}`,
    marginTop: 14,
    paddingTop: 12,
  },
  footerIcon: {
    color: colors.GREY_3,
    flexShrink: 0,
  },
  footerLead: {
    fontSize: 13,
    fontWeight: 700,
    color: colors.GREY_1,
  },
  footerText: {
    fontSize: 13,
    color: colors.GREY_3,
    flex: 1,
  },
  footerLink: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 700,
    color: colors.GREY_1,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { color: colors.BRIGHT_BLUE },
  },
})

export default SetupRecommendationsCard
